import React, { useEffect, useMemo, useRef, useState } from 'react';

import downDisabledIcon from '@assets/img/icons/down-disabled.svg';
import downIcon from '@assets/img/icons/down.svg';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import CheckIcon from '@material-ui/icons/Check';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { type ClassMap, makeMuiStyles, useCommonStyles, usePalette } from '@styles';
import { formStrings } from '@utils/forms';
import { sanitizeJapaneseNumber } from '@utils/sanitize';
import clsx from 'clsx';
import Color from 'color';
import dayjs from 'dayjs';
import useDebounce from 'react-use/lib/useDebounce';

const useStyles = makeMuiStyles((theme, common) => ({
  textInput: {
    ...common.middleText,
    ...common.flexInline,
    alignItems: 'center',
    borderStyle: 'solid',
    borderWidth: 1,
    padding: '10px 15px',
    flex: 1,
    resize: 'vertical',
    position: 'relative',
    borderRadius: 4,
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.background.paper,
    outline: 'none',
    [theme.breakpoints.down('sm')]: {
      ...common.middleText,
      padding: '5px 5px',
    },
    '&$bordered': {
      borderColor: theme.palette.grey['500'],
    },
    '&$focused': {
      borderColor: theme.palette.primary.main,
    },
    '&$disabled': {
      backgroundColor: theme.palette.grey['100'],
    },
    '&$error': {
      borderColor: theme.palette.error.main,
    },
    '&$choose:not($disabled)': {
      cursor: 'pointer',
    },
    '&::placeholder': {
      textOverflow: 'ellipsis !important',
      color: theme.palette.grey[400],
    },
    lineHeight: 1.8,
  },
  input: {
    ...common.middleText,
    flex: 1,
    width: 0,
    borderStyle: 'none',
    borderWidth: 0,
    outline: 'none',
    display: 'block',
    '$disabled &': {
      backgroundColor: theme.palette.grey['100'],
    },
    '$choose &:not($disabled &)': {
      cursor: 'pointer !important',
    },
    '&::placeholder': {
      textOverflow: 'ellipsis !important',
      color: theme.palette.grey[400],
    },
    paddingLeft: 0,
    paddingRight: 0,
    height: 'initial',
  },
  autoCompleteItem: {
    ...common.middleText,
    overflow: 'visible',
  },
  autoCompleteRoot: {
    ...common.middleText,
    position: 'absolute',
    top: '105%',
    left: 0,
    width: '100%',
    zIndex: 9999999,
    maxHeight: '30vh',
    overflow: 'auto',
  },
  selectorItems: {
    zIndex: 9999999,
    width: '100%',
  },
  selectorItemsPaper: {
    width: '100%',
    overflow: 'auto',
    maxHeight: '30vh',
  },
  menuItem: {
    ...common.middleText,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  menuListRoot: {
    minWidth: 'max-content',
  },
  selectedItem: {
    backgroundColor: `${Color(theme.palette.primary.main).alpha(0.05).toString()} !important`,
  },
  bordered: {},
  error: {},
  focused: {},
  disabled: {},
  choose: {},
  optionGroupLabel: {
    ...common.middleText,
    padding: '0.25rem 0.5rem',
    margin: '0.25rem 0.5rem',
  },
  optionGroupIndicator: {
    position: 'absolute',
    top: '0.6rem',
    left: '0.5rem',
    height: 'calc(100% - 1.2rem)',
    width: '2px',
    backgroundColor: theme.palette.primary.main,
  },
  datePickerInputRoot: {
    ...common.flex1,
    '& > div > button': {
      padding: '0',
    },
  },
  noBorder: {
    border: 'none',
  },
  adornedEnd: {
    padding: '0',
  },
}));

export interface TextBoxOption {
  label: string;
  value: string | number | boolean;
}

export interface TextBoxOptionGroup {
  label: string;
  children: TextBoxOption[];
}

export interface SemiAutoCompleteOption {
  label: string;
  value: any;
}
export interface TextBoxOwnProps {
  multiline?: boolean;
  iconBefore?: React.ReactNode;
  iconAfter?: React.ReactNode;
  classes?: ClassMap<'input' | 'textInput'>;
  hasError?: boolean;
  showPassword?: boolean;
  withAutoComplete?: boolean;
  autoCompleteChoices?: string[];
  autoCompleteWidth?: number;
  withSemiAutoComplete?: boolean;
  semiAutoCompleteOptions?: SemiAutoCompleteOption[];
  onSemiAutoCompleteOptionSelect?: (option: SemiAutoCompleteOption) => void;
  setValue?: (v: string | boolean | number) => void;
  setTouched?: () => void;
  onValueChange?: (v: string | boolean | number) => void;
  bordered?: boolean;
  choose?: boolean;
  options?: (TextBoxOptionGroup | TextBoxOption)[];
  prefix?: string;
  coercion?: 'hankaku';
  multiChoose?: boolean;
}

function isOptionGroup(option: TextBoxOptionGroup | TextBoxOption): option is TextBoxOptionGroup {
  return Object.prototype.hasOwnProperty.call(option, 'children');
}

function flatOptions(options: (TextBoxOptionGroup | TextBoxOption)[]): TextBoxOption[] {
  return options.flatMap((option) => {
    if (isOptionGroup(option)) {
      return option.children;
    }
    return option;
  });
}

export interface AutoCompleteItemsProps {
  autoCompleteChoices?: string[];
  onSetValue?: (v: string) => void;
}

const AutoCompleteItems: React.FC<AutoCompleteItemsProps> = React.memo((props) => {
  const { autoCompleteChoices, onSetValue } = props;

  const classes = useStyles();

  return (
    <Paper classes={{ root: classes.autoCompleteRoot }}>
      {autoCompleteChoices?.map((choice) => (
        <MenuItem
          key={choice}
          onClick={() => onSetValue?.(choice)}
          classes={{
            root: classes.autoCompleteItem,
          }}
        >
          {choice}
        </MenuItem>
      ))}
    </Paper>
  );
});

export interface SemiAutoCompleteItemsProps {
  autoCompleteWidth?: number;
  semiAutoCompleteOptions?: SemiAutoCompleteOption[];
  onSemiAutoCompleteOptionSelect?: (option: SemiAutoCompleteOption) => void;
  onClickAway: (event: React.MouseEvent<EventTarget>) => void;
  close: () => void;
}

const SemiAutoCompleteItems: React.FC<SemiAutoCompleteItemsProps> = React.memo((props) => {
  const {
    semiAutoCompleteOptions,
    onSemiAutoCompleteOptionSelect,
    autoCompleteWidth,
    onClickAway,
    close,
  } = props;

  const classes = useStyles();
  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Paper classes={{ root: classes.autoCompleteRoot }} style={{ width: autoCompleteWidth }}>
        {semiAutoCompleteOptions?.map((option) => (
          <MenuItem
            key={option.label}
            autoFocus={false}
            onClick={() => {
              onSemiAutoCompleteOptionSelect?.(option);
              close();
            }}
            classes={{
              root: classes.autoCompleteItem,
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Paper>
    </ClickAwayListener>
  );
});

export interface SelectorItemsProps {
  open?: Boolean;
  anchorEl?: HTMLInputElement | null;
  selectorOptions?: (TextBoxOptionGroup | TextBoxOption)[];
  onSelectorOptionSelect?: (option: string | number | boolean) => void;
  onClickAway: (event: React.MouseEvent<EventTarget>) => void;
  selectedValue?: string | number | readonly string[];
}

const SelectorItems: React.FC<SelectorItemsProps> = React.memo((props) => {
  const { open, anchorEl, selectorOptions, onSelectorOptionSelect, onClickAway, selectedValue } =
    props;

  const classes = { ...useStyles(), ...useCommonStyles() };

  const renderItem = ({ label, value: optionValue }: TextBoxOption) => {
    const isSelected =
      selectedValue instanceof Array
        ? selectedValue.includes(optionValue as string)
        : optionValue === selectedValue;

    return (
      <MenuItem
        selected={isSelected}
        autoFocus={false}
        key={String(optionValue)}
        className={classes.menuItem}
        classes={{ selected: classes.selectedItem }}
        onClick={() => {
          onSelectorOptionSelect?.(optionValue);
        }}
      >
        <div
          className={clsx(
            classes.flexInline,
            classes.flex1,
            classes.justifyBetween,
            classes.itemsCenter,
          )}
        >
          <span className={classes.flex1}>{label}</span>
          {isSelected && (
            <>
              <span style={{ width: '0.5rem' }} />
              <CheckIcon color="primary" fontSize="inherit" />
            </>
          )}
        </div>
      </MenuItem>
    );
  };

  return (
    <Popper
      open={Boolean(open)}
      anchorEl={anchorEl}
      role={'menu'}
      popperOptions={{ placement: 'bottom-start' }}
      disablePortal
      className={classes.selectorItems}
    >
      <Paper className={classes.selectorItemsPaper}>
        <ClickAwayListener onClickAway={onClickAway}>
          <MenuList
            autoFocus={Boolean(open)}
            autoFocusItem={Boolean(open)}
            variant={'selectedMenu'}
            classes={{ root: classes.menuListRoot }}
          >
            {selectorOptions?.map((option, index) => {
              if (isOptionGroup(option)) {
                return (
                  <div key={index}>
                    <div style={{ position: 'relative' }}>
                      <div className={classes.optionGroupIndicator} />
                      <div className={classes.optionGroupLabel}>{option.label}</div>
                    </div>
                    {option.children.map((childOption) => {
                      return renderItem(childOption);
                    })}
                  </div>
                );
              }
              return renderItem(option);
            })}
          </MenuList>
        </ClickAwayListener>
      </Paper>
    </Popper>
  );
});

export type TextBoxProps =
  | (TextBoxOwnProps & React.HTMLProps<HTMLInputElement>)
  | (TextBoxOwnProps & React.HTMLProps<HTMLTextAreaElement>);

export const TextBox: React.FC<TextBoxProps> = (props) => {
  const {
    multiline = false,
    iconBefore = null,
    iconAfter = null,
    classes: customClasses = {},
    hasError = false,
    withAutoComplete = false,
    bordered = true,
    autoCompleteChoices = [],
    withSemiAutoComplete = false,
    autoCompleteWidth,
    semiAutoCompleteOptions = [],
    type,
    value = '',
    setValue,
    setTouched,
    onSemiAutoCompleteOptionSelect,
    choose = false,
    multiChoose = false,
    options = [],
    readOnly,
    prefix,
    required,
    autoComplete,
    coercion,
    ...rest
  } = props;

  const { disabled } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLInputElement>(null);
  const classes = { ...useStyles(), ...useCommonStyles() };
  const palette = usePalette();

  const [autoCompleteOpen, setAutoCompleteOpen] = useState(false);
  const [filteredAutoCompleteItems, setAutoCompleteItems] = useState<string[]>([]);
  const [semiAutoCompleteOpen, setSemiAutoCompleteOpen] = useState(false);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [multiSelectorOpen, setMultiSelectorOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const normalizedAutoCompleteChoices = useMemo(
    () => autoCompleteChoices.map((c) => c.toLowerCase()),
    [autoCompleteChoices],
  );

  const setValueRef = useRef(setValue);
  setValueRef.current = setValue;

  const constSetValue: typeof setValue = useMemo(() => (v) => setValueRef?.current?.(v), []);

  useDebounce(
    () => {
      const normalizedValue = value.toString().trim().toLowerCase();
      requestIdleCallback(() => {
        if (withAutoComplete) {
          const p1 = window.performance.now();
          const choices: string[] = [];

          let i = 0;

          for (const choice of autoCompleteChoices) {
            if (window.performance.now() - p1 > 20) {
              return;
            }

            if (choice !== value && normalizedAutoCompleteChoices[i].startsWith(normalizedValue)) {
              choices.push(choice);
            }
            i++;
          }
          setAutoCompleteItems(choices);
        }
      });
    },
    200,
    [value, autoCompleteChoices],
  );

  useEffect(() => {
    // Trigger an input event so that autofill can work.
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current!.value = inputRef.current!.value;
      }
    }, 100);
  }, []);

  const onClick = () => {
    if (choose && !disabled) {
      setSelectorOpen((prevOpen) => !prevOpen);
    }
    if (multiChoose) setMultiSelectorOpen(true);
  };

  const onClickAway = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setSemiAutoCompleteOpen(false);
    setSelectorOpen(false);
    setMultiSelectorOpen(false);
  };

  const iconStyle = {
    paddingLeft: '0.5rem',
    color: palette.grey['300'],
    display: 'block',
    marginTop: -10,
    marginBottom: -10,
  };

  const renderTogglePassword = () => {
    return (
      <div onClick={() => setShowPassword(!showPassword)}>
        {showPassword ? (
          <VisibilityIcon style={iconStyle} />
        ) : (
          <VisibilityOffIcon style={iconStyle} />
        )}
      </div>
    );
  };

  const renderToggleMenu = () => {
    return (
      <img
        alt="Toggle Menu"
        src={disabled ? downDisabledIcon : downIcon}
        style={{
          ...iconStyle,
          paddingRight: '0.25rem',
        }}
      />
    );
  };

  const finalIconAfter =
    iconAfter ??
    (type === 'password'
      ? renderTogglePassword()
      : choose || multiChoose
      ? renderToggleMenu()
      : null);

  const renderDatePicker = () => {
    return (
      <KeyboardDatePicker
        invalidDateMessage={''}
        format="YYYY/MM/DD"
        name={rest.name}
        onChange={async (date) => {
          setValue?.(dayjs.isDayjs(date) && date.isValid() ? date.format('YYYY-MM-DD') : '');
          await Promise.resolve();
          setTouched?.();
        }}
        autoOk
        value={!(value instanceof Array) && dayjs(value).isValid() ? value : null}
        inputVariant={'outlined'}
        disabled={disabled}
        readOnly={readOnly}
        className={classes.flex1}
        InputProps={{
          style: rest.style,
          classes: {
            input: clsx(classes.input, customClasses.input),
            notchedOutline: classes.noBorder,
            adornedEnd: classes.adornedEnd,
            root: classes.datePickerInputRoot,
          },
        }}
        disableToolbar
        views={['year', 'month', 'date']}
        cancelLabel={formStrings.cancel}
        minDate={props.min}
        maxDate={props.max}
      />
    );
  };

  const renderInput = () => (
    <input
      size={1}
      ref={inputRef}
      className={clsx(classes.input, customClasses.input)}
      value={choose ? flatOptions(options).find((o) => o.value == value)?.label ?? '' : value}
      {...(rest as React.HTMLProps<HTMLInputElement>)}
      onFocus={(e) => {
        if (withAutoComplete) setAutoCompleteOpen(true);
        if (withSemiAutoComplete) setSemiAutoCompleteOpen(true);
        setFocused(true);
        rest.onFocus?.(e as any);
      }}
      {...(coercion === 'hankaku' && {
        onChange: (e) => {
          e.target.value = sanitizeJapaneseNumber(e.target.value);
          rest.onChange?.(e as any);
        },
      })}
      onBlur={(e) => {
        setTimeout(() => {
          if (withAutoComplete) setAutoCompleteOpen(false);
        }, 200);
        setFocused(false);
        rest.onBlur?.(e as any);
      }}
      type={showPassword ? 'text' : type}
      readOnly={Boolean(readOnly) || choose || multiChoose}
      aria-required={Boolean(required)}
      aria-invalid={hasError}
      autoComplete={autoCompleteChoices.length > 0 || withSemiAutoComplete ? 'off' : autoComplete}
    />
  );

  const divClasses = clsx(
    classes.textInput,
    bordered && classes.bordered,
    hasError && classes.error,
    focused && classes.focused,
    rest.disabled && classes.disabled,
    (choose || multiChoose) && classes.choose,
    customClasses.textInput,
  );

  return (
    <>
      {type === 'date' && <div className={divClasses}>{renderDatePicker()}</div>}
      {!multiline && type !== 'date' && (
        <div className={divClasses} ref={anchorRef} onClick={onClick}>
          {iconBefore}
          {!withSemiAutoComplete && withAutoComplete && autoCompleteOpen && (
            <AutoCompleteItems
              autoCompleteChoices={filteredAutoCompleteItems}
              onSetValue={constSetValue}
            />
          )}
          {!withAutoComplete && withSemiAutoComplete && semiAutoCompleteOpen && (
            <SemiAutoCompleteItems
              autoCompleteWidth={autoCompleteWidth}
              semiAutoCompleteOptions={semiAutoCompleteOptions || []}
              onSemiAutoCompleteOptionSelect={onSemiAutoCompleteOptionSelect}
              onClickAway={onClickAway}
              close={() => setSemiAutoCompleteOpen(false)}
            />
          )}
          {(choose || multiChoose) && (
            <SelectorItems
              selectedValue={value}
              open={choose ? selectorOpen : multiSelectorOpen}
              anchorEl={anchorRef.current!}
              onSelectorOptionSelect={constSetValue}
              selectorOptions={options}
              onClickAway={onClickAway}
            />
          )}
          {prefix}
          {renderInput()}

          {finalIconAfter}
        </div>
      )}
      {multiline && (
        <textarea
          className={divClasses}
          rows={3}
          value={value}
          readOnly={Boolean(readOnly)}
          aria-required={Boolean(required)}
          aria-invalid={hasError}
          {...(rest as React.HTMLProps<HTMLTextAreaElement>)}
          onFocus={(e) => {
            setFocused(true);
            rest.onFocus?.(e as any);
          }}
          onBlur={(e) => {
            setFocused(false);
            rest.onBlur?.(e as any);
          }}
        />
      )}
    </>
  );
};
